'use client';

import { useState, useEffect } from 'react';
import { facultiesData } from '@/data/faculties';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '@/context/LanguageContext';

const FacultyImage = ({ src, alt, className }: { src: string, alt: string, className?: string }) => {
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      className={`w-full h-full ${className}`}
      onError={() => setCurrentSrc(`https://via.placeholder.com/200x100/004d26/FFCC00?text=${encodeURIComponent(alt)}`)}
    />
  );
};

export default function FacultiesSection() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { t, language } = useTranslation();

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="akademik" className="py-24 bg-unmulDarkGreen text-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-serif font-bold">
            {t('faculties.title.faculties').toUpperCase()} <span className="text-unmulYellow">{t('faculties.title.programs').toUpperCase()}</span>
          </h2>
          <p
            className="text-green-200 mt-4 max-w-3xl mx-auto text-lg"
            dangerouslySetInnerHTML={{ __html: t('faculties.description') }}
          />
        </div>

        {/* Faculty List - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-x-12 gap-y-6">
          {facultiesData.map((faculty, index) => {
            const currentName = language === 'en' && faculty.nameEn ? faculty.nameEn : faculty.name;
            const currentPrograms = language === 'en' && faculty.programsEn ? faculty.programsEn : faculty.programs;

            return (
              <div
                key={faculty.id}
                className="flex gap-4 group"
                data-aos="fade-up"
                data-aos-delay={index * 30}
              >
                {/* Number */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-unmulYellow text-unmulDarkGreen rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                    {index + 1}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 pb-6 border-b border-white/20">
                  {/* Faculty Name */}
                  <a
                    href={faculty.slug}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-bold text-unmulYellow hover:text-white transition uppercase tracking-wide flex items-center gap-2"
                  >
                    {currentName}
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs opacity-0 group-hover:opacity-100 transition" />
                  </a>

                  {/* Programs */}
                  {currentPrograms && currentPrograms.length > 0 && (
                    <div className="mt-2">
                      <p className={`text-sm text-green-100 leading-relaxed ${expandedId === faculty.id ? '' : 'line-clamp-2'}`}>
                        {currentPrograms.join(', ')}
                      </p>
                      {currentPrograms.length > 4 && (
                        <button
                          onClick={() => toggleExpand(faculty.id)}
                          className="text-xs text-unmulYellow hover:underline mt-1 font-medium"
                        >
                          {expandedId === faculty.id ? `← ${t('faculties.card.hide')}` : `${t('faculties.card.showProdi', { count: currentPrograms.length })} →`}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Image */}
                <div className="hidden md:block flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden shadow-lg relative">
                  <FacultyImage
                    src={faculty.image}
                    alt={currentName}
                    className="object-cover group-hover:scale-110 transition duration-500"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Stats */}
        <div className="mt-16 pt-8 border-t border-white/20" data-aos="fade-up">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-center">
            <div>
              <div className="text-5xl font-bold text-unmulYellow">15</div>
              <div className="text-sm text-green-200 uppercase tracking-wider mt-1">{t('faculties.stats.faculties')}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-unmulYellow">100+</div>
              <div className="text-sm text-green-200 uppercase tracking-wider mt-1">{t('faculties.stats.program')}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-unmulYellow">S1-S3</div>
              <div className="text-sm text-green-200 uppercase tracking-wider mt-1">{t('faculties.stats.level')}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-unmulYellow">5</div>
              <div className="text-sm text-green-200 uppercase tracking-wider mt-1">{t('faculties.stats.profession')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
